import type { ReactElement } from "react";
import { color } from "@lumi/tokens";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  sm: "w-6 h-6",
  md: "w-8 h-8",
  lg: "w-12 h-12",
};

/**
 * Lumi-branded loading spinner with smooth rotation animation
 * Inspired by streaming platform loading indicators
 * Uses Tailwind's animate-spin for performance
 */
export function LoadingSpinner({ size = "md", className = "" }: Readonly<LoadingSpinnerProps>): ReactElement {
  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <div
        className={`${sizeClasses[size]} border-2 border-neutral-700 border-t-amber-400 rounded-full animate-spin`}
        style={{
          borderTopColor: color.accent,
        }}
      />
    </div>
  );
}

interface LoadingDotsProps {
  className?: string;
}

/**
 * Subtle loading dots animation for text-based loading states
 * Uses CSS animations for performance
 */
export function LoadingDots({ className = "" }: Readonly<LoadingDotsProps>): ReactElement {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className="w-1 h-1 bg-current rounded-full animate-pulse" style={{ animationDelay: "0s" }} />
      <span className="w-1 h-1 bg-current rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
      <span className="w-1 h-1 bg-current rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
    </span>
  );
}

interface ProgressBarProps {
  progress?: number; // 0-100
  className?: string;
  indeterminate?: boolean;
}

/**
 * Streaming platform style progress bar for video loading
 * Uses CSS transitions and animations for performance
 */
export function ProgressBar({
  progress = 0,
  className = "",
  indeterminate = false,
}: Readonly<ProgressBarProps>): ReactElement {
  return (
    <div className={`w-full bg-neutral-800 rounded-full h-1 overflow-hidden ${className}`}>
      <div
        className={`h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-[width] duration-300 ease-out ${
          indeterminate ? "animate-[shimmer_1.5s_ease-in-out_infinite]" : ""
        }`}
        style={{
          width: indeterminate ? "30%" : `${progress}%`,
          background: `linear-gradient(90deg, ${color.accent}, #fbbf24)`,
        }}
      />
    </div>
  );
}
